import React from 'react';
import type { FitMetric } from './types';

interface Props {
  metrics: FitMetric[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onUnlock: () => void;
}

interface Band {
  label: string;
  widthPct: number;
  ideal: boolean;
  here: boolean;
}

/** Split a metric's zones into proportional bands and mark the one the rider is in. */
function toBands(metric: FitMetric): Band[] {
  if (!metric.scale || metric.zones.length === 0) return [];
  const span = metric.scale.hi - metric.scale.lo;
  if (span <= 0) return [];

  let from = metric.scale.lo;
  const bands = metric.zones.map(z => {
    const band: Band = {
      label: z.label,
      // Clamp at zero: a zero-length zone (z.to === from) must never go negative.
      widthPct: Math.max(0, ((z.to - from) / span) * 100),
      ideal: z.ideal === true,
      here: metric.raw !== null && metric.raw > from && metric.raw <= z.to,
    };
    from = z.to;
    return band;
  });

  // A value at or beyond an edge falls in no band above; pin it to the nearest.
  if (!bands.some(b => b.here) && metric.raw !== null) {
    const idx = metric.raw <= metric.scale.lo ? 0 : bands.length - 1;
    bands[idx].here = true;
  }
  return bands;
}

function caretPct(metric: FitMetric): number | null {
  if (!metric.scale || metric.raw === null) return null;
  const span = metric.scale.hi - metric.scale.lo;
  if (span <= 0) return null;
  const pct = ((metric.raw - metric.scale.lo) / span) * 100;
  return Math.min(100, Math.max(0, pct));
}

/** A zero-width band must not carry the `- 2px` gap subtraction — that would
 * produce a negative flex-basis, which browsers clamp or ignore. */
function bandFlexBasis(widthPct: number): string {
  return widthPct <= 0 ? '0 0 0' : `0 0 calc(${widthPct}% - 2px)`;
}

const FitMetrics: React.FC<Props> = ({ metrics, selectedId, onSelect, onUnlock }) => (
  <div className="fr-metrics">
    {metrics.map(metric => {
      const isOpen = selectedId === metric.id;
      const bands = toBands(metric);
      const caret = caretPct(metric);
      const panelId = `fr-metric-panel-${metric.id}`;

      return (
        <div
          key={metric.id}
          className={
            `fr-metric fr-metric--${metric.status}` + (isOpen ? ' fr-metric--open' : '')
          }
        >
          <button
            type="button"
            className="fr-metric__button"
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => onSelect(isOpen ? null : metric.id)}
          >
            <span className="fr-metric__head">
              <span className="fr-metric__title">{metric.headline}</span>
              <span className="fr-metric__value">{metric.value}</span>
            </span>

            {bands.length > 0 && (
              <>
                <span className="fr-track">
                  {bands.map((b, i) => (
                    <span
                      key={i}
                      className={
                        'fr-track__seg' +
                        (b.ideal ? ' fr-track__seg--ideal' : '') +
                        (b.here ? ' fr-track__seg--here' : '')
                      }
                      style={{ flex: bandFlexBasis(b.widthPct) }}
                    />
                  ))}
                  {caret !== null && (
                    <span className="fr-track__caret" style={{ left: `${caret}%` }} />
                  )}
                </span>
                <span className="fr-zonelabels">
                  {bands.map((b, i) => (
                    <span
                      key={i}
                      className={'fr-zonelabel' + (b.here ? ' fr-zonelabel--here' : '')}
                      style={{ flex: bandFlexBasis(b.widthPct) }}
                    >
                      {b.label}
                    </span>
                  ))}
                </span>
              </>
            )}

            <span className="fr-metric__foot">
              <span className="fr-metric__ideal">{metric.ideal_label}</span>
              <span className="fr-metric__more">
                {isOpen ? 'Hide' : 'What this means'}
              </span>
            </span>
          </button>

          {isOpen && (
            <div id={panelId} className="fr-metric__panel">
              {/* Re-film guidance is FREE — the server never strips it, even when
                  the paid explanation is withheld. It tells the rider how to fix
                  a failed scan, which is camera advice, not fit interpretation.
                  Guidance renders before explanation when both are present —
                  unreachable from the server today (the two are mutually
                  exclusive by construction) but specified, so pinned here. */}
              {metric.guidance && (
                <p className="fr-metric__guidance">{metric.guidance}</p>
              )}
              {metric.explanation && (
                <p className="fr-metric__explain">{metric.explanation}</p>
              )}
              {!metric.explanation && !metric.guidance && (
                <p className="fr-metric__explain">
                  The number is yours. What it means for your riding — and what to do about
                  it — is part of the full plan.{' '}
                  <button type="button" className="fr-lock-link" onClick={onUnlock}>
                    Unlock the full plan
                  </button>
                </p>
              )}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

export default FitMetrics;
